import { useEffect, useRef, useState } from "react";
import { MessageSquarePlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { displayCompanyName } from "@/lib/displayCompanyName";
import { useLoadNotes, useMyCompanyName } from "@/hooks/useLoadNotes";

interface Props {
  loadId: string;
  /** Which side the current viewer is on. */
  viewerRole: "shipper" | "broker";
  viewerUserId: string;
  /** Full legal name fallback when the viewer's company can't be resolved. */
  fallbackCompanyName?: string | null;
  /** Optional visual tone classes (fill/border/shadow). Defaults to unchanged styling. */
  cardClassName?: string;
}

function relativeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export function DispatchLoadNotesCard({ loadId, viewerRole, viewerUserId, fallbackCompanyName, cardClassName }: Props) {
  const { notes, isLoading, addNote } = useLoadNotes(loadId);
  const { data: myCompanyName } = useMyCompanyName(viewerUserId);
  const [text, setText] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);

  const counterparty = viewerRole === "shipper" ? "broker" : "shipper";

  // Keep the running log pinned to the newest note.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [notes.length]);

  const post = async () => {
    if (!text.trim() || addNote.isPending) return;
    try {
      await addNote.mutateAsync({
        body: text,
        authorUserId: viewerUserId,
        authorRole: viewerRole,
        authorCompanyName: myCompanyName ?? fallbackCompanyName ?? null,
      });
      setText(""); // cleared only on a confirmed write
    } catch (e: any) {
      // Silent-write guard: surface the failure, keep the typed text.
      toast.error(e?.message || "Could not post note");
    }
  };

  return (
    <Card className={`p-3 mb-4 ${cardClassName ?? ""}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <MessageSquarePlus className="h-4 w-4 text-brand-teal" />
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Load Notes
          </span>
        </div>
        {notes.length > 0 && (
          <span className="text-[11px] text-muted-foreground tabular-nums">
            {notes.length} {notes.length === 1 ? "note" : "notes"}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className="h-16 animate-pulse rounded bg-muted" />
      ) : notes.length === 0 ? (
        <p className="text-xs text-muted-foreground py-3">
          No notes yet. Add a note about this load — both you and the {counterparty} will see it.
        </p>
      ) : (
        <div ref={scrollRef} className="max-h-[320px] overflow-y-auto space-y-2 pr-1 mb-3">
          {notes.map((n) => {
            const mine = n.author_user_id === viewerUserId;
            const isShipper = n.author_role === "shipper";
            return (
              <div
                key={n.id}
                className={
                  "rounded-md border p-2 " +
                  (mine ? "border-brand-teal/30 bg-brand-teal/5" : "border-border bg-muted/30")
                }
              >
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span
                    className={
                      "inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide " +
                      (isShipper
                        ? "bg-brand-teal/15 text-brand-teal"
                        : "bg-slate-500/15 text-slate-600")
                    }
                  >
                    {isShipper ? "Shipper" : "Broker"}
                  </span>
                  <span className="text-xs font-medium text-foreground truncate">
                    {displayCompanyName(n.author_company_name) ||
                      (isShipper ? "Shipper" : "Broker")}
                  </span>
                  {mine && <span className="text-[10px] text-muted-foreground">(you)</span>}
                  <span className="text-[11px] text-muted-foreground ml-auto">
                    {relativeAgo(n.created_at)}
                  </span>
                </div>
                <p className="text-xs text-foreground whitespace-pre-wrap break-words">{n.body}</p>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
        <Textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={2000}
          rows={2}
          placeholder={`Add a note for the ${counterparty} (status update, instructions, gate code...)`}
          className="text-xs resize-none"
        />
        <Button
          size="sm"
          className="sm:w-auto"
          disabled={!text.trim() || addNote.isPending}
          onClick={post}
        >
          {addNote.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
          Post note
        </Button>
      </div>
    </Card>
  );
}

export default DispatchLoadNotesCard;
